"use client";

import { CitationTool } from "@/components/citation-tool";
import {
  type ExampleColumns,
  exampleRows,
} from "@/components/tools/example-columns";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const MAGAZINE_CITATION_GENERATOR_STRINGS = {
  examplesHeading: "Magazine Citation Examples",
  inTextPrefix: "In-text: ",
  // Columns, not rows: see `example-columns.ts`. Unlike the interview tool's, these are
  // slot schemas rather than worked citations, so `example` translates too.
  examples: {
    style: ["APA 7th edition", "MLA 9th edition", "Chicago (author-date)"],
    label: ["Reference list", "Works Cited", "Reference list"],
    example: [
      "Author, A. A. (Year, Month Day). Article title. Magazine Name, Volume(Issue), pages. https://example.com/article",
      'Last, First. "Article Title." Magazine Name, Day Month Year, pp. 12-15.',
      'Last, First. Year. "Article Title." Magazine Name, Month Day, pages.',
    ],
    inText: ["(Author, Year)", "(Last 13)", "(Last Year)"],
  } as ExampleColumns,
} as const;

/**
 * Live citation generator embedded on the Magazine landing page. The tool opens on the
 * manual-entry tab preset to the Magazine article (CSL `article-magazine`) source type.
 * Users type the author, article title, magazine name, and date and pick any of the
 * 10,834 styles.
 */
export default function MagazineCitationGeneratorWidget() {
  const strings = useToolUi(MAGAZINE_CITATION_GENERATOR_STRINGS);

  return (
    <>
      <CitationTool
        initialManualType="article-magazine"
        initialMode="manual"
        styles={getFeaturedStyles()}
      />

      {/* Examples */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.examplesHeading}
        </h2>
        <div className="space-y-4">
          {exampleRows(
            MAGAZINE_CITATION_GENERATOR_STRINGS.examples,
            strings.examples
          ).map((ex) => (
            <div
              className="rounded-lg border border-border bg-muted/30 p-4"
              key={ex.style}
            >
              <p className="mb-1 font-semibold text-foreground text-sm">
                {ex.style}
                <span className="ml-2 font-normal text-muted-foreground">
                  {ex.label}
                </span>
              </p>
              <p className="font-mono text-foreground text-sm leading-relaxed">
                {ex.example}
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                {strings.inTextPrefix}
                <span className="font-mono">{ex.inText}</span>
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
